import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import './VirtualDesk.css';
import './StudentTest.css';
import API_URL from '../api';
import DeskEngine from './DeskEngine';
import { getInitialStudentInfo, checkAutoRegister } from '../utils/queryPrefill';

const StudentTest = () => {
    const { hash } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const completionStorageKey = `desk-test-completed:${hash}`;
    const [testData, setTestData] = useState(null);
    const [dishes, setDishes] = useState([]);
    const [loading, setLoading] = useState(true);

    // Registration State
    const [isRegistered, setIsRegistered] = useState(false);
    const [studentInfo, setStudentInfo] = useState(() =>
        getInitialStudentInfo(location.search)
    );
    const [testResult, setTestResult] = useState(null);
    const [startTime] = useState(Date.now());

    const [availableCities, setAvailableCities] = useState([]);

    useEffect(() => {
        const fetchInitialData = async () => {
            try {
                const citiesRes = await axios.get(`${API_URL}/cities`);
                setAvailableCities(citiesRes.data);
            } catch (error) {
                console.error('Error fetching initial data:', error);
            }
        };
        fetchInitialData();
    }, []);

    useEffect(() => {
        if (!testData) return;

        const allowedItems = testData.templateId?.allowedItems || [];
        setDishes(allowedItems.map((item) => ({
            ...item,
            _id: item.id || item.type,
            id: item.id || item.type,
        })));
    }, [testData]);

    useEffect(() => {
        const fetchTest = async () => {
            try {
                const response = await axios.get(`${API_URL}/tests/${hash}${location.search}`);
                if (response.data.usageMode === 'single' && localStorage.getItem(completionStorageKey) === '1' && !new URLSearchParams(location.search).get('assigned')) {
                    navigate('/inactive');
                    return;
                }
                setTestData(response.data);
                
                const cityBindingEnabled = response.data.city && response.data.cityBindingEnabled;
                const defaults = cityBindingEnabled ? { city: response.data.city } : {};
                const info = getInitialStudentInfo(location.search, defaults);
                
                setStudentInfo(info);
                if (checkAutoRegister(info, cityBindingEnabled)) {
                    setIsRegistered(true);
                }
            } catch (error) {
                console.error('Error fetching test:', error);
                if (error.response?.status === 410) navigate('/inactive');
            } finally {
                setLoading(false);
            }
        };
        fetchTest();
    }, [hash, navigate, completionStorageKey, location.search]);

    const handleRegister = (e) => {
        e.preventDefault();
        if (studentInfo.firstName && studentInfo.lastName && studentInfo.position) {
            setIsRegistered(true);
        } else {
            alert('Будь ласка, заповніть усі поля');
        }
    };

    const handleDeskSubmit = async (items) => {
        const isAssigned = new URLSearchParams(location.search).get('assigned') === '1';
        const payload = {
            items,
            studentName: studentInfo.firstName,
            studentLastName: studentInfo.lastName,
            studentCity: studentInfo.city,
            studentPosition: studentInfo.position,
            externalId: studentInfo.externalId,
            webhookUrl: sessionStorage.getItem('serviq_webhook_url') || '',
            timeSpent: Math.round((Date.now() - startTime) / 1000),
            allowRetake: isAssigned
        };
        const response = await axios.post(`${API_URL}/tests/${hash}/submit`, payload);
        localStorage.setItem(completionStorageKey, '1');
        const { score, total, percentage, passed, validatedItems, ghostItems, issuedCertificateId } = response.data;
        return { score, total, percentage, passed, validatedItems, ghostItems: ghostItems || [], issuedCertificateId };
    };

    const handleDeskResult = (result) => {
        setTestResult(result);
    };

    if (loading) return <div className="placeholder-view">Завантаження тесту...</div>;
    if (!testData) return <div className="placeholder-view">Тест не знайдено</div>;

    if (!isRegistered) {
        return (
            <div className="registration-container">
                <div className="registration-card">
                    <h2>Реєстрація на тест</h2>
                    <p>Шаблон: <strong>{testData.templateId.name}</strong></p>
                    {(testData.description || testData.templateId?.description) && (
                        <div className="test-task-description">
                            📋 <strong>Завдання:</strong> {testData.description || testData.templateId.description}
                        </div>
                    )}
                    <form onSubmit={handleRegister}>
                        <div className="form-group">
                            <label>Ім'я</label>
                            <input type="text" value={studentInfo.firstName}
                                onChange={(e) => setStudentInfo({ ...studentInfo, firstName: e.target.value })}
                                placeholder="Введіть ім'я" required />
                        </div>
                        <div className="form-group">
                            <label>Прізвище</label>
                            <input type="text" value={studentInfo.lastName}
                                onChange={(e) => setStudentInfo({ ...studentInfo, lastName: e.target.value })}
                                placeholder="Введіть прізвище" required />
                        </div>
                        <div className="form-group">
                            <label>Місто</label>
                            {testData.cityBindingEnabled ? (
                                <input type="text" value={studentInfo.city} disabled className="form-control-disabled" />
                            ) : (
                                <select value={studentInfo.city}
                                    onChange={(e) => setStudentInfo({ ...studentInfo, city: e.target.value })}
                                    required className="form-control">
                                    <option value="">Оберіть місто...</option>
                                    {availableCities.map(city => (
                                        <option key={city._id} value={city.name}>{city.name}</option>
                                    ))}
                                </select>
                            )}
                        </div>
                        <div className="form-group">
                            <label>Посада</label>
                            <input type="text" value={studentInfo.position}
                                onChange={(e) => setStudentInfo({ ...studentInfo, position: e.target.value })}
                                placeholder="Введіть посаду" required />
                        </div>
                        <button type="submit" className="btn-save-template btn-start-test">
                            Почати тест
                        </button>
                    </form>
                </div>
            </div>
        );
    }

    return (
        <div>
            <div className="student-test-header">
                <h1>🍽️ {testData.templateId.name}</h1>
                <p>{studentInfo.firstName} {studentInfo.lastName} · {studentInfo.city} ({studentInfo.position})</p>
            </div>
            <DeskEngine
                dishes={dishes}
                underlays={[]}
                description={testData.description || testData.templateId?.description}
                timeLimit={testData.templateId?.timeLimit || 0}
                deskSurfacePreset={testData.templateId?.deskSurfacePreset || 'transparent'}
                deskSurfaceColor={testData.templateId?.deskSurfaceColor || '#ffffff'}
                onSubmit={handleDeskSubmit}
                onResult={handleDeskResult}
            />
            {testResult && (
                <div className="student-test-result-msg success">
                    <div className="result-text">Адміністратор отримав ваші результати. Результат: <strong>{testResult.percentage}%</strong></div>
                    {testResult.issuedCertificateId && (
                        <a
                            href={`/verify-certificate/${testResult.issuedCertificateId}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="btn-certificate-download"
                        >
                            🎓 Отримати сертифікат (PDF)
                        </a>
                    )}
                    <button
                        type="button"
                        className="btn-save-template btn-start-test"
                        style={{ marginTop: '1rem' }}
                        onClick={() => {
                            window.location.href = '/dashboard';
                        }}
                    >
                        ✓ Повернутися до навчання
                    </button>
                </div>
            )}
        </div>
    );
};

export default StudentTest;
